import { createSlice } from '@reduxjs/toolkit';
import { REMEMBER_REHYDRATED } from 'redux-remember';
import appTypes from '../app/types';
import FEATURES from './features';
const initialState = {
    availableCredits: 0,
    totalCredits: 0,
    bulkAvailableCredits: 0,
    isAccountAdmin: false,
    isSearchByDataPointEnabled: false,
    isOneWalletEnabled: false,
    features: [],
    company: { isLoaded: false },
    analytics: {},
};
/** `new Date(undefined)` is an Invalid Date and throws on `toISOString`, so every conversion is guarded. */
const toIsoString = (value) => {
    if (!value) {
        return undefined;
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};
const mapBilling = (user) => {
    var _a;
    const { billing } = user;
    if (!billing) {
        return undefined;
    }
    const [plan = {}] = user.accountBillingPlans || [];
    return {
        category: billing.category,
        isPaid: billing.isPaid,
        endDate: toIsoString(billing.endDate),
        renewalType: billing.renewalType,
        renews: (_a = billing.renews) !== null && _a !== void 0 ? _a : true,
        stripeInvoiceLink: plan.stripeInvoiceLink,
        stripePaymentMethodCountryCode: plan.stripePaymentMethodCountryCode,
        billingPlanId: plan.billingPlanId,
        startDate: toIsoString(plan.startDate),
    };
};
const mapAnalytics = (analytics = {}) => {
    var _a;
    const registerDate = toIsoString(analytics.registerDate);
    return {
        firstProspectingScoutSearchDate: toIsoString(analytics.firstProspectingScoutSearchDate),
        firstProspectingFullShowDate: toIsoString(analytics.firstProspectingFullShowDate),
        registerDate,
        lastTermsAndConditionsApprovalDate: (_a = toIsoString(analytics.lastTermsAndConditionsApprovalDate)) !== null && _a !== void 0 ? _a : registerDate,
        firstShowDate: toIsoString(analytics.firstShowDate),
        firstSearchDate: toIsoString(analytics.firstSearchDate),
        firstOutOfCredits: toIsoString(analytics.firstOutOfCredits),
    };
};
const slice = createSlice({
    name: 'USER',
    initialState,
    extraReducers(builder) {
        builder.addCase(appTypes.RESET, () => initialState);
        builder.addCase(REMEMBER_REHYDRATED, state => (Object.assign(Object.assign({}, initialState), state)));
    },
    reducers: {
        /** Mirrors `User.setUser` in MST: takes the raw `/users/me` payload and flattens it onto the slice. */
        setUser(state, { payload: user }) {
            var _a, _b, _c, _d, _e, _f;
            const features = user.features ? [...user.features] : [];
            const [plan = {}] = user.accountBillingPlans || [];
            state.id = user.id;
            state.accountId = user.accountId;
            state.firstName = user.firstName;
            state.lastName = user.lastName;
            state.countryCode = (_a = user.countryCode) !== null && _a !== void 0 ? _a : undefined;
            state.email = user.email;
            state.phone = (_b = user.phone) === null || _b === void 0 ? void 0 : _b.toString();
            state.hashedEmail = user.hashedEmail;
            state.industry = user.industry;
            state.accountDomain = user.domain;
            state.groupName = (_c = user.group) === null || _c === void 0 ? void 0 : _c.name;
            state.roleId = (_d = user.role) === null || _d === void 0 ? void 0 : _d.id;
            state.isAccountAdmin = Boolean(user.admin);
            state.features = features;
            state.isSearchByDataPointEnabled =
                features.includes(FEATURES.SHOW_SPECIFIC_DATA_POINTS) && !features.includes(FEATURES.SCALE_FREE_TRIAL);
            state.isOneWalletEnabled = features.includes(FEATURES.ONE_WALLET);
            state.priceBook = plan.priceBook;
            state.accountUnfreezeDate = plan.endDate;
            state.subscriptionStatus = plan.subscriptionStatus;
            state.idleLogoutTimeMinutes = (_f = (_e = user.accountSettings) === null || _e === void 0 ? void 0 : _e.idleLogoutTimeMinutes) !== null && _f !== void 0 ? _f : undefined;
            if (user.account) {
                state.accountStatus = user.account.status || 'active';
                state.accountStatusReason = user.account.statusReason;
            }
            state.billing = mapBilling(user);
            state.analytics = mapAnalytics(user.analytics);
        },
        setCompanyInfo(state, { payload }) {
            state.company = payload;
        },
        setBulkAvailableCredits(state, { payload }) {
            state.bulkAvailableCredits = payload !== null && payload !== void 0 ? payload : 0;
        },
        onCreditUsed(state) {
            --state.availableCredits;
        },
        /** Auth reported the session as gone: drops the user, which also clears its persisted copy. */
        loggedOut() {
            return initialState;
        },
    },
});
export const { reducer, actions } = slice;
export default reducer;
